import { invoke } from "@tauri-apps/api/core";
import { Cron } from "croner";
import { type Getter, atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomWithRefresh, loadable } from "jotai/utils";
import { nanoid } from "nanoid";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { $documentLocalPath } from "../document-list/document-list.atom";
import {
	$registerDocument,
	$registeredDocumentIds,
	$unregisterDocument,
} from "../document-loader/registry.atom";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { env } from "../env/env";
import { $featureFlags } from "../feature-flags";
import { Flag } from "../feature-flags/feature-flags.types";
import { $editorState, $editorStateEffect } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { store } from "../store";
import { $internet, $simulateOffline } from "../utils/internet.atom";
import { computeWorkflowSchedule } from "./compute-workflow-schedule";
import type {
	Workflow,
	WorkflowAgent,
	WorkflowRun,
	WorkflowScheduleEntry,
} from "./workflows.types";

const logger = new Logger("workflows");

const DOCUMENT_READY_TIMEOUT_MS = 30_000;
const DOCUMENT_READY_POLL_MS = 200;

const waitForDocumentReady = async (get: Getter, documentId: string): Promise<boolean> => {
	const metadataKey = metadataKeyForDocsRoute(documentId);
	const deadline = Date.now() + DOCUMENT_READY_TIMEOUT_MS;

	logger.info("Waiting for document to be ready", { documentId });

	while (Date.now() < deadline) {
		const metadata = get($documentMetadata(metadataKey));
		if (!metadata) {
			await new Promise((r) => setTimeout(r, DOCUMENT_READY_POLL_MS));
			continue;
		}

		const pages = metadata.pagesMetadata ?? [];
		const markdownPages = pages.filter((p) => p.type === undefined || p.type === "markdown");
		const loadedCount = markdownPages.filter((page) => {
			const key = editorStateKeyForDocsRoute({ documentId, pageId: page.id });
			return get($editorState(key)) !== null;
		}).length;

		if (loadedCount === markdownPages.length) {
			logger.info("Document ready", {
				documentId,
				pageCount: markdownPages.length,
			});
			return true;
		}

		logger.debug("Waiting for editor states", {
			documentId,
			loaded: loadedCount,
			total: markdownPages.length,
		});
		await new Promise((r) => setTimeout(r, DOCUMENT_READY_POLL_MS));
	}
	return false;
};

interface ScriptOutput {
	status: number | null;
	stdout: number[];
	stderr: number[];
}

const escapeShellArg = (arg: string): string => {
	if (env.isWindows) {
		return `'${arg.replace(/'/g, "''")}'`;
	}
	return `'${arg.replace(/'/g, "'\\''")}'`;
};

const buildAgentCommand = (agent: WorkflowAgent, prompt: string): string => {
	const escaped = escapeShellArg(prompt);
	switch (agent) {
		case "claude":
			return `echo ${escaped} | claude -p --dangerously-skip-permissions`;
		case "amp":
			return `echo ${escaped} | amp -x --dangerously-allow-all`;
		case "codex":
			return `echo ${escaped} | codex --quiet --full-auto`;
		case "copilot":
			return `echo ${escaped} | copilot -p`;
		case "shell":
			return prompt;
	}
};

const buildScript = (workflow: Workflow): string => {
	const command = buildAgentCommand(workflow.agent, workflow.prompt);
	if (env.isWindows) {
		return command;
	}
	return `#!/bin/bash\n${command}`;
};

// Internal refreshable atom for cached workflows
const $cachedWorkflows = atomWithRefresh(async () => {
	const cache = getCache();

	try {
		const workflows = await cache.workflows.getAll();
		return Object.values(workflows);
	} catch (error) {
		logger.error("Failed to get cached workflows", { cause: error });
		return [];
	}
});
$cachedWorkflows.debugLabel = "cachedWorkflows";

export const $workflows = atom(
	async (get): Promise<Workflow[]> => {
		return get($cachedWorkflows);
	},
	async (get, set, update: Workflow | { delete: string }) => {
		const cache = getCache();

		if ("delete" in update) {
			await cache.workflows.delete(update.delete);
		} else {
			await cache.workflows.upsert(update);
		}

		set($cachedWorkflows);
	}
);
$workflows.debugLabel = "workflows";

// Internal refreshable atom for cached workflow runs
const $cachedWorkflowRuns = atomWithRefresh(async () => {
	const cache = getCache();

	try {
		const runs = await cache.workflowRuns.getAll();
		return runs.sort(
			(a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
		);
	} catch (error) {
		logger.error("Failed to get cached workflow runs", { cause: error });
		return [];
	}
});
$cachedWorkflowRuns.debugLabel = "cachedWorkflowRuns";

export const $workflowRuns = atom(
	async (get): Promise<WorkflowRun[]> => {
		return get($cachedWorkflowRuns);
	},
	async (get, set, update: WorkflowRun) => {
		const cache = getCache();
		await cache.workflowRuns.upsert(update);
		set($cachedWorkflowRuns);
	}
);
$workflowRuns.debugLabel = "workflowRuns";

export const $deleteDocumentWorkflows = atom(null, async (_get, set, documentId: string) => {
	const cache = getCache();
	await cache.workflows.deleteByDocumentId(documentId);
	set($cachedWorkflows);
	set($cachedWorkflowRuns);
});
$deleteDocumentWorkflows.debugLabel = "deleteDocumentWorkflows";

let didCleanupOrphanedRuns = false;

export const $cleanupOrphanedRuns = atom(null, async (_get, set) => {
	if (didCleanupOrphanedRuns) {
		return;
	}

	const cache = getCache();
	try {
		const count = await cache.workflowRuns.cleanUpOrphanedRuns();
		didCleanupOrphanedRuns = true;
		if (count > 0) {
			logger.info("Cleaned up orphaned workflow runs", { count });
			set($cachedWorkflowRuns);
		}
	} catch (error) {
		logger.error("Failed to clean up orphaned workflow runs", { cause: error });
	}
});
$cleanupOrphanedRuns.debugLabel = "cleanupOrphanedRuns";

// Coarse time signal (~30s) for schedule recomputation.
// We don't need the 1s resolution of $now — schedule checks only matter
// at the granularity of cron ticks (minimum 1 minute).
const $coarseNow = atomWithRefresh(() => new Date());
$coarseNow.debugPrivate = true;
$coarseNow.onMount = (set) => {
	const interval = setInterval(() => set(), 30_000);
	return () => clearInterval(interval);
};

/**
 * Derived read-only atom: the full schedule state for every workflow.
 *
 * Combines `$cachedWorkflows`, `$workflowRuns`, `$internet`, and a coarse
 * time signal into `WorkflowScheduleEntry[]` via the pure
 * `computeWorkflowSchedule()` function.
 *
 * The scheduler effect and UI components both read from this atom.
 */
export const $workflowSchedule = atom(async (get): Promise<WorkflowScheduleEntry[]> => {
	const workflows = await get($cachedWorkflows);
	const runs = await get($cachedWorkflowRuns);
	const isOnline = get($internet) && !get($simulateOffline);
	const now = get($coarseNow);

	return computeWorkflowSchedule(workflows, runs, isOnline, now);
});
$workflowSchedule.debugLabel = "workflowSchedule";

const SUPPORTED_API_VERSIONS = new Set(["v1alpha1"]);

export const $runWorkflow = atom(null, async (get, set, workflow: Workflow) => {
	const runId = nanoid();
	const now = new Date().toISOString();

	if (!SUPPORTED_API_VERSIONS.has(workflow.apiVersion)) {
		logger.warn("Skipping workflow with unsupported apiVersion", {
			workflowId: workflow.id,
			apiVersion: workflow.apiVersion,
		});

		await set($workflowRuns, {
			id: runId,
			automationId: workflow.id,
			status: "failed" as const,
			error: `Unsupported workflow apiVersion: ${workflow.apiVersion}`,
			startedAt: now,
			completedAt: now,
		});
		return;
	}

	const run: WorkflowRun = {
		id: runId,
		automationId: workflow.id,
		status: "running",
		startedAt: now,
	};

	await set($workflowRuns, run);

	// Register the target document so the document loader activates its editor
	// states and collab connections. This ensures the file watcher can apply
	// agent-written changes to the live ProseMirror state (and sync via collab).
	const effectUnsubs: (() => void)[] = [];
	let didRegister = false;
	if (workflow.targetDocumentId) {
		didRegister = !get($registeredDocumentIds).has(workflow.targetDocumentId);
		set($registerDocument, workflow.targetDocumentId);

		const ready = await waitForDocumentReady(get, workflow.targetDocumentId);
		if (!ready) {
			logger.warn("Timed out waiting for document to load, proceeding anyway", {
				documentId: workflow.targetDocumentId,
			});
		}

		// Mount editor state effects so collab sync (sendToCollab) and disk
		// persistence actually run. Normally these are mounted by the editor UI
		// component, but workflows load documents without rendering them.
		const metadataKey = metadataKeyForDocsRoute(workflow.targetDocumentId);
		const metadata = get($documentMetadata(metadataKey));
		const pages = metadata?.pagesMetadata ?? [];
		for (const page of pages) {
			if (page.type !== undefined && page.type !== "markdown") {
				continue;
			}
			const key = editorStateKeyForDocsRoute({
				documentId: workflow.targetDocumentId,
				pageId: page.id,
			});
			logger.info("Mounting editor state effect for workflow", {
				documentId: workflow.targetDocumentId,
				pageId: page.id,
			});
			effectUnsubs.push(store.sub($editorStateEffect(key), () => {}));
		}
	}

	try {
		// Resolve cwd from targetDocumentId if set
		let cwd: string | undefined;
		if (workflow.targetDocumentId) {
			const docPath = await get($documentLocalPath(workflow.targetDocumentId));
			if (docPath) {
				cwd = docPath;
			} else {
				logger.warn("Target document has no local path", {
					documentId: workflow.targetDocumentId,
				});
			}
		}

		const output = await invoke<ScriptOutput>("run_script", {
			script: new TextEncoder().encode(buildScript(workflow)),
			timeoutSecs: 0,
			cwd,
		});

		const stdout = new TextDecoder().decode(new Uint8Array(output.stdout));
		const stderr = new TextDecoder().decode(new Uint8Array(output.stderr));

		// Agent CLIs (amp, claude, codex, copilot) may exit 0 even when they
		// encounter HTTP/API errors (e.g., 402 payment required). Detect these
		// by checking stderr for lines like "Error: 402 {...}".
		const hasAgentError =
			workflow.agent !== "shell" && !!stderr && /^Error: \d{3}\b/m.test(stderr);
		const succeeded = output.status === 0 && !hasAgentError;

		await set($workflowRuns, {
			...run,
			status: succeeded ? "completed" : "failed",
			output: stdout || undefined,
			error: stderr || undefined,
			completedAt: new Date().toISOString(),
		});
	} catch (error) {
		logger.error("Failed to run workflow", { workflowId: workflow.id, error });

		await set($workflowRuns, {
			...run,
			status: "failed",
			error: error instanceof Error ? error.message : String(error),
			completedAt: new Date().toISOString(),
		});
	} finally {
		for (const unsub of effectUnsubs) {
			unsub();
		}
		if (workflow.targetDocumentId && didRegister) {
			set($unregisterDocument, workflow.targetDocumentId);
		}
	}
});
$runWorkflow.debugLabel = "runWorkflow";

/**
 * Scheduling-relevant fingerprint for a workflow entry.
 * Only fields that affect *which* Cron timers should exist.
 */
const scheduleFingerprint = (e: WorkflowScheduleEntry): string =>
	`${e.workflow.id}|${e.workflow.schedule}|${e.workflow.enabled}|${e.scheduleStatus}`;

// Ref for the previous set of fingerprints, used by the scheduler effect to
// avoid tearing down and recreating Cron jobs when only non-scheduling fields
// change (e.g., a run completing).
let prevFingerprints: string | undefined;
let activeJobs: Cron[] = [];
// Track workflows with in-flight catch-up runs to prevent duplicates.
// When catch-up fires $runWorkflow (async), the run status may not be persisted
// before the effect re-runs, causing isDue/status to still look stale.
const pendingCatchUps = new Set<string>();

const stopActiveJobs = () => {
	for (const job of activeJobs) {
		job.stop();
	}
	activeJobs = [];
};

const resetSchedulerState = () => {
	stopActiveJobs();
	// Reset fingerprints so jobs are recreated when the effect runs again
	// (e.g., after feature flag is re-enabled).
	prevFingerprints = undefined;
};

export const $workflowSchedulerEffect = atomEffect((get, set) => {
	if (!env.isDesktopApp) {
		resetSchedulerState();
		return;
	}

	const flagsLoadable = get(loadable($featureFlags));
	if (flagsLoadable.state !== "hasData" || !flagsLoadable.data[Flag.Workflows]) {
		resetSchedulerState();
		return;
	}

	// Clean up any runs left in "running"/"pending" from a previous session
	set($cleanupOrphanedRuns);

	const scheduleLoadable = get(loadable($workflowSchedule));
	if (scheduleLoadable.state !== "hasData") {
		// Keep existing Cron jobs alive while the schedule is recomputing.
		// Do NOT return a cleanup — atomEffect would call it on the next re-run,
		// which would kill jobs right when the resolved data triggers re-execution.
		return;
	}

	const schedule = scheduleLoadable.data;

	// Ref-based diff: only rebuild Cron timers when scheduling-relevant fields change.
	const currentFingerprints = schedule.map(scheduleFingerprint).sort().join("\n");

	if (currentFingerprints !== prevFingerprints) {
		prevFingerprints = currentFingerprints;

		// Tear down old jobs, build new ones
		stopActiveJobs();

		for (const entry of schedule) {
			const { workflow, scheduleStatus } = entry;
			if (scheduleStatus !== "active") {
				continue;
			}

			try {
				const workflowId = workflow.id;
				const job = new Cron(workflow.schedule, async () => {
					// Look up the latest workflow object at execution time so edits
					// to prompt/name/etc. are picked up without rebuilding Cron timers.
					const workflows = await store.get($cachedWorkflows);
					const current = workflows?.find((w) => w.id === workflowId);
					if (current) {
						set($runWorkflow, current);
					}
				});
				activeJobs.push(job);
			} catch (error) {
				logger.error("Failed to create cron job for workflow", {
					workflowId: workflow.id,
					schedule: workflow.schedule,
					error,
				});
			}
		}
	}

	// Catch-up runs live outside the fingerprint gate: after sleep/wake, isDue
	// changes (now is later) but fingerprints stay the same since isDue is not
	// a scheduling-relevant field. Running catch-up on every effect invocation
	// ensures missed ticks are handled even without an online/offline transition.
	for (const entry of schedule) {
		if (
			entry.isDue &&
			entry.scheduleStatus === "active" &&
			entry.status !== "running" &&
			!pendingCatchUps.has(entry.workflow.id)
		) {
			logger.info("Running catch-up for missed workflow tick", {
				workflowId: entry.workflow.id,
			});
			pendingCatchUps.add(entry.workflow.id);
			void set($runWorkflow, entry.workflow).finally(() => {
				pendingCatchUps.delete(entry.workflow.id);
			});
		}
	}

	// No cleanup returned — jobs are torn down via stopActiveJobs() when fingerprints
	// change and new jobs are created. This effect lives for the app lifetime so
	// unmount cleanup is not needed. Returning a cleanup here would cause atomEffect
	// to kill jobs on every re-execution (before the new body runs), leaving a window
	// with no active timers.
});
$workflowSchedulerEffect.debugLabel = "workflowSchedulerEffect";
